import {
	ActionButton,
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
	Text,
} from '@react-spectrum/s2';
import AlertTriangle from '@react-spectrum/s2/icons/AlertTriangle';
import More from '@react-spectrum/s2/icons/More';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import moment from 'moment';
import { useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { useUserId } from '@/auth/UserIdContext';
import { EmailBodyMarkdown } from '@/components/EmailBodyEditor/EmailBodyMarkdown.tsx';
import type { EffectiveStatus } from '@/hooks/useContentArtifacts';
import { logger } from '@/logger';
import { normalizeToUtcIsoZ } from '@/spaces/chat/panels/scheduleUtils';
import { trackEvent } from '@/utils/fullstory';

import type { Action } from '../../../../bridge/types/action.ts';
import type {
	EmailHtml,
	EmailPlaintext,
} from '../../../../bridge/types/artifact.ts';

import { ContentDetailStatusCallout } from './ContentDetailStatusCallout';
import { dismissDeletedArtifact } from './contentDetailUtils';
import { DeleteConfirmationWrapper } from './DeleteConfirmationWrapper';
import { emailSendDate } from './emailSendDate';
import { EmailThumbnail } from './EmailThumbnail';
import { SchedulePopover } from './SchedulePopover';
import { useArtifactEditChat } from './useArtifactEditChat';

interface EmailContentDetailProps {
	artifact: EmailHtml | EmailPlaintext;
	action?: Action;
	effectiveStatus: EffectiveStatus;
	refresh?: () => Promise<void>;
}

const getDefaultScheduleDraft = (action: Action | undefined): string => {
	if (action?.trigger?.type === 'scheduled_once') {
		return action.trigger.timestamp;
	}
	return moment().add(1, 'minute').toISOString();
};

const ScheduledStatusCallout = ({
	artifact,
	action,
	effectiveStatus,
}: {
	artifact: EmailHtml | EmailPlaintext;
	action?: Action;
	effectiveStatus: EffectiveStatus;
}) => {
	const isProcessing = effectiveStatus === 'processing';
	const isScheduled = effectiveStatus === 'scheduled';
	const isPublished = effectiveStatus === 'published';

	if (!isProcessing && !isScheduled && !isPublished) return null;

	const sendDate = emailSendDate(artifact, action);
	const dateTime = sendDate?.format('MM/DD/YY [at] h:mma');
	const status = isProcessing
		? 'processing'
		: isPublished
			? 'published'
			: 'scheduled';
	const label = isProcessing
		? dateTime
			? `Sending at ${dateTime}`
			: 'Sending'
		: isPublished
			? dateTime
				? `Sent at ${dateTime}`
				: 'Sent'
			: dateTime
				? `Scheduled for ${dateTime}`
				: 'Scheduled';

	return (
		<ContentDetailStatusCallout
			status={status}
			label={label}
			progressAriaLabel="Sending"
		/>
	);
};

export const EmailContentDetail = ({
	artifact,
	action,
	effectiveStatus,
	refresh,
}: EmailContentDetailProps) => {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isScheduling, setIsScheduling] = useState(false);
	const [isUnscheduling, setIsUnscheduling] = useState(false);
	const [mutationError, setMutationError] = useState<string | null>(null);
	const [showScheduleEditor, setShowScheduleEditor] = useState(false);
	const [scheduleDraft, setScheduleDraft] = useState(() =>
		getDefaultScheduleDraft(action),
	);

	const hasAction = !!action;

	const patchArtifactStatus = async (status: 'scheduled' | 'draft') => {
		if (!userId) throw new Error('Missing user id');
		await bridgewayApi.patch(`/api/user/${userId}/artifacts/${artifact.id}`, {
			status,
		});
	};

	const handleEditEmail = useArtifactEditChat(artifact);

	const openScheduleEditor = () => {
		setMutationError(null);
		setScheduleDraft(getDefaultScheduleDraft(action));
		setShowScheduleEditor(true);
	};

	const handleConfirmSchedule = async () => {
		if (!action) return;
		const timestamp = normalizeToUtcIsoZ(scheduleDraft);
		setIsScheduling(true);
		setMutationError(null);
		try {
			bridgeApi.sendAction({
				type: 'do_edit_process_action',
				action_id: action.id,
				updates: { trigger: { type: 'scheduled_once', timestamp } },
			});
			await patchArtifactStatus('scheduled');
			trackEvent('Content Email Scheduled', { artifact_id: artifact.id });
			setShowScheduleEditor(false);
			await refresh?.();
		} catch (error) {
			setMutationError('Failed to schedule email. Please try again.');
			logger.error('Failed to schedule email', error);
		} finally {
			setIsScheduling(false);
		}
	};

	const handleUnscheduleEmail = async () => {
		if (!action) return;
		setIsUnscheduling(true);
		setMutationError(null);
		try {
			bridgeApi.sendAction({
				type: 'do_edit_process_action',
				action_id: action.id,
				updates: { trigger: { type: 'on_activation' } },
			});
			await patchArtifactStatus('draft');
			trackEvent('Content Email Unscheduled', { artifact_id: artifact.id });
			await refresh?.();
		} catch (error) {
			setMutationError('Failed to unschedule email. Please try again.');
			logger.error('Failed to unschedule email', error);
		} finally {
			setIsUnscheduling(false);
		}
	};

	const handleDeleteEmail = async () => {
		if (!userId) return;
		setIsDeleting(true);
		setMutationError(null);
		try {
			await bridgewayApi.patch(`/api/user/${userId}/artifacts/${artifact.id}`, {
				status: 'cancelled',
			});
			trackEvent('Content Email Deleted', { artifact_id: artifact.id });
			setShowDeleteConfirmation(false);
			await refresh?.();
			dismissDeletedArtifact(bridgeApi, artifact.id);
		} catch (error) {
			setMutationError('Failed to delete email. Please try again.');
			logger.error('Failed to delete email', error);
		} finally {
			setIsDeleting(false);
		}
	};

	const showScheduleControls =
		effectiveStatus === 'scheduled' || effectiveStatus === 'paused';
	const showDraftControls =
		effectiveStatus === 'draft' || effectiveStatus === 'failed';
	const showPublishedControls = effectiveStatus === 'published';
	const isProcessing = effectiveStatus === 'processing';

	const errorText = mutationError ?? (isProcessing ? 'Send in progress' : null);

	return (
		<div className="flex h-full min-h-0 flex-col gap-6">
			<div className="flex items-center gap-3">
				<div className="min-w-0 flex-1">
					<ScheduledStatusCallout
						artifact={artifact}
						action={action}
						effectiveStatus={effectiveStatus}
					/>
				</div>
				<div className="flex shrink-0 items-center gap-4">
					{errorText && (
						<span className="flex items-center gap-2">
							<AlertTriangle />
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body',
									color: 'body',
								})}
							>
								{errorText}
							</Text>
						</span>
					)}
					<MenuTrigger>
						<SchedulePopover
							isOpen={showScheduleEditor}
							scheduleDraft={scheduleDraft}
							onScheduleDraftChange={setScheduleDraft}
							onCancel={() => setShowScheduleEditor(false)}
							onConfirm={handleConfirmSchedule}
							heading="Schedule email"
							confirmLabel="Schedule"
							heldOnWholeMinute={true}
						>
							<DeleteConfirmationWrapper
								onConfirm={handleDeleteEmail}
								onCancel={() => setShowDeleteConfirmation(false)}
								isOpen={showDeleteConfirmation}
							>
								<ActionButton isQuiet aria-label="More options">
									<More />
								</ActionButton>
							</DeleteConfirmationWrapper>
						</SchedulePopover>
						<Popover>
							<Menu>
								{showScheduleControls && (
									<>
										<MenuItem id="edit-scheduled" onPress={handleEditEmail}>
											<Text>Edit</Text>
										</MenuItem>
										<MenuItem
											id="reschedule"
											onPress={openScheduleEditor}
											isDisabled={!hasAction || isScheduling}
										>
											<Text>Reschedule</Text>
										</MenuItem>
										<MenuItem
											id="unschedule"
											onPress={handleUnscheduleEmail}
											isDisabled={!hasAction || isUnscheduling}
										>
											<Text>Unschedule</Text>
										</MenuItem>
									</>
								)}
								{showDraftControls && (
									<>
										<MenuItem id="edit-draft" onPress={handleEditEmail}>
											<Text>Edit Draft</Text>
										</MenuItem>
										<MenuItem
											id="schedule"
											onPress={openScheduleEditor}
											isDisabled={!hasAction || isScheduling}
										>
											<Text>Schedule</Text>
										</MenuItem>
									</>
								)}
								{showPublishedControls && (
									<MenuItem id="edit-published" onPress={handleEditEmail}>
										<Text>Edit</Text>
									</MenuItem>
								)}
								<MenuItem
									id="delete"
									onPress={() => setShowDeleteConfirmation(true)}
									isDisabled={isDeleting}
								>
									<Text>Delete</Text>
								</MenuItem>
							</Menu>
						</Popover>
					</MenuTrigger>
				</div>
			</div>

			<div className="flex min-h-0 flex-1 flex-col">
				{artifact.type === 'email_html' ? (
					artifact.content.instance?.cosmos_id ? (
						<EmailThumbnail artifact={artifact} size="detail" />
					) : (
						<div className="glass-card-sm p-4">
							<pre className="whitespace-pre-wrap text-(--text-primary)">
								{artifact.content.body}
							</pre>
						</div>
					)
				) : (
					<div className="glass-card-sm p-4">
						<EmailBodyMarkdown body={artifact.content.body} />
					</div>
				)}
			</div>
		</div>
	);
};
